import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { useMemo } from 'react';

import { SIDEBAR_APPS } from '@verantos-dev/core-constants';
import {
  EvidencePlatformSidebarAppID,
  UserRolesIdsEnum,
  ProjectsSidebarAppFeatureId,
} from '@verantos-dev/core-enums';

import useAuth from './useAuth';

// Duplicated from app-state-base to avoid circular dependency
const useFeatureFlagSplit = (featureFlag: string, isMultivariate?: boolean): boolean | string => {
  // evaluate feature flags with the `useSplitTreatments` hook
  const { treatments, isReady } = useSplitTreatments({ names: [featureFlag] });
  if (!isReady) {
    return false;
  }
  const { treatment } = treatments[featureFlag];
  if (isMultivariate) {
    return treatment;
  } else {
    return treatment === 'on';
  }
};

export const useAllowedApps = () => {
  const isDatasetDeliveryEnabled = useFeatureFlag('RLORDatasetDeliveryUI');
  const isDataReliabilityPageEnabled = useFeatureFlag('RLORDataReliabilityUI');
  const isConceptGroupsEnabled = useFeatureFlagSplit('concept_group_management_list');
  // TODO: add feature flag for concept groups
  const {
    user: { roles, isInternalUser },
  } = useAuth();
  const sidebarAppIds = roles.reduce((_apps, _role) => {
    switch (_role.roleId) {
      case UserRolesIdsEnum.OR_RESEARCHER:
        return [
          EvidencePlatformSidebarAppID.projects,
          ...(isConceptGroupsEnabled ? [EvidencePlatformSidebarAppID.conceptGroups] : []),
          ..._apps,
        ];
      case UserRolesIdsEnum.SA_ADMIN:
        return [
          EvidencePlatformSidebarAppID.users,
          ...(isInternalUser ? [EvidencePlatformSidebarAppID.organizations] : []),
          ..._apps,
        ];
      case UserRolesIdsEnum.VRN_DATA_ADMIN:
        return [
          ...(isDatasetDeliveryEnabled ? [EvidencePlatformSidebarAppID.dataAdministration] : []),
          ..._apps,
        ];
      default:
        return _apps;
    }
  }, [] as EvidencePlatformSidebarAppID[]);

  return useMemo(() => {
    return SIDEBAR_APPS.filter((app) => sidebarAppIds.includes(app.appId)).map((app) => {
      if (app.appId === EvidencePlatformSidebarAppID.users && !isInternalUser) {
        return {
          ...app,
          additionalItems: [],
        };
      }
      // remove data reliability if flag is off
      if (app.appId === EvidencePlatformSidebarAppID.projects && !isDataReliabilityPageEnabled) {
        return {
          ...app,
          features: app.features?.filter(
            (item) => item.featureId !== ProjectsSidebarAppFeatureId.dataReliability
          ),
        };
      }
      return {
        ...app,
      };
    });
  }, [sidebarAppIds, isInternalUser, isDataReliabilityPageEnabled]);
};
